import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { BASE_PAYLOAD } from '@routes/sidebar/admin-center/___shared___/constants.ts';
import type {
    CommonSubscriptionsRequest,
    Subscription,
    SubscriptionsResultsDescription,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/subscriptions/api/subscriptions.api.ts';
import {
    downloadApiSubscriptionsCsv,
    getApiSubscriptions,
    putSubscriptionActivation,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/subscriptions/api/subscriptions.api.ts';
import type { PaginatedResponse } from '@types';
import { downloadFromDisposition } from '@utils/download.ts';
import { extractError } from '@utils/slice.ts';

interface SubscriptionsError {
    message?: string;
    [k: string]: any;
}

interface SubscriptionsState {
    error: SubscriptionsError | null;
    commonSubscriptions: PaginatedResponse<Subscription, SubscriptionsResultsDescription> | null;
    subscriptions: Subscription[];
    subscriptionsRequestData: CommonSubscriptionsRequest;
    showDeactivateSubscriptionErrorBanner: boolean;
    currentGetRequestId: string | null;

    isGetSubscriptionsPending: boolean;
    isUpdateActivationStatePending: boolean;
    isExportSubscriptionsCsvPending: boolean;
}

const INITIAL_SUBSCRIPTIONS_REQUEST: CommonSubscriptionsRequest = {
    ...BASE_PAYLOAD,
    pageNumber: 1,
    pageSize: 10,
};

const initialState: SubscriptionsState = {
    error: null,
    commonSubscriptions: null,
    subscriptions: [],
    subscriptionsRequestData: INITIAL_SUBSCRIPTIONS_REQUEST,
    showDeactivateSubscriptionErrorBanner: false,
    isGetSubscriptionsPending: false,
    currentGetRequestId: null,

    isUpdateActivationStatePending: false,
    isExportSubscriptionsCsvPending: false,
};

export const getSubscriptions = createAsyncThunk<
    PaginatedResponse<Subscription, SubscriptionsResultsDescription>,
    { clientId: number; tenantAlias: string; payload: CommonSubscriptionsRequest },
    { rejectValue: SubscriptionsError }
>('subscriptions/getSubscriptions', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiSubscriptions(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateSubscriptionActivation = createAsyncThunk<
    any,
    { clientId: number; tenantAlias: string; adminPathwayId: string; isActive: boolean },
    { rejectValue: SubscriptionsError }
>(
    'subscriptions/updateSubscriptionActivation',
    ({ clientId, tenantAlias, adminPathwayId, isActive }, { rejectWithValue }) =>
        putSubscriptionActivation(clientId, tenantAlias, adminPathwayId, { isActive })
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const downloadSubscriptionsCsv = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string },
    { rejectValue: SubscriptionsError }
>('subscriptions/downloadSubscriptionsCsv', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    downloadApiSubscriptionsCsv(clientId, tenantAlias)
        .then((res) => {
            downloadFromDisposition(res);
        })
        .catch((err) => rejectWithValue(err)),
);

const subscriptionsSlice = createSlice({
    name: 'subscriptions',
    initialState,
    reducers: {
        setShowDeactivateSubscriptionErrorBanner(state, action: PayloadAction<boolean>) {
            state.showDeactivateSubscriptionErrorBanner = action.payload;
        },
        clearSubscriptions(state) {
            state.subscriptions = [];
            state.commonSubscriptions = null;
            state.subscriptionsRequestData = INITIAL_SUBSCRIPTIONS_REQUEST;
            state.showDeactivateSubscriptionErrorBanner = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getSubscriptions.pending, (state, action) => {
            state.isGetSubscriptionsPending = true;
            state.currentGetRequestId = action.meta.requestId;
            state.subscriptionsRequestData = action.meta.arg.payload;
            state.error = null;
        });
        builder.addCase(getSubscriptions.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetSubscriptionsPending = false;
            state.currentGetRequestId = null;
            state.commonSubscriptions = action.payload;
            state.subscriptions = action.payload.data;
        });
        builder.addCase(getSubscriptions.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetSubscriptionsPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(updateSubscriptionActivation.pending, (state) => {
            state.isUpdateActivationStatePending = true;
            state.error = null;
        });
        builder.addCase(updateSubscriptionActivation.fulfilled, (state, _action) => {
            state.isUpdateActivationStatePending = false;
            // state.subscriptions = action.payload.data;
        });
        builder.addCase(updateSubscriptionActivation.rejected, (state, action) => {
            state.isUpdateActivationStatePending = false;
            state.error = extractError(action);
        });

        builder.addCase(downloadSubscriptionsCsv.pending, (state) => {
            state.isExportSubscriptionsCsvPending = true;
            state.error = null;
        });
        builder.addCase(downloadSubscriptionsCsv.fulfilled, (state) => {
            state.isExportSubscriptionsCsvPending = false;
        });
        builder.addCase(downloadSubscriptionsCsv.rejected, (state, action) => {
            state.isExportSubscriptionsCsvPending = false;
            state.error = extractError(action);
        });
    },
});

export const { setShowDeactivateSubscriptionErrorBanner, clearSubscriptions } = subscriptionsSlice.actions;

export default subscriptionsSlice.reducer;
